import {
  useEffect, useRef, useState, MouseEvent,
} from 'react';
import {
  alpha, Box, Menu, MenuItem, Skeleton, useTheme,
} from '@mui/material';
import { blue, blueGrey, green } from '@mui/material/colors';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../../common/library/Button/Button';
import { PlaybookResponse } from '../../../common/models/content/Playbook';
import * as contentAPI from '../../../api/contentAPI';
import { TestIds } from '../../../assets/testIds';
import { Logger } from '../../../config/logging/logger';
import { usePlaybookHistory } from './usePlaybookHistory';
import { useIsDesktop } from '../../../common/hooks/useResponsive';

const logger = Logger.create('PlaybookActions');
const testIds = TestIds.playbookView;

// Per the Figma, each option (and the button once it's chosen) has its own color
const RESPONSE_COLORS: Record<PlaybookResponse, string> = {
  [PlaybookResponse.Yes]: green[700],
  [PlaybookResponse.ToDo]: blue[700],
  [PlaybookResponse.No]: blueGrey[700],
};
// Figma button text: 15px medium, 26px line height, 0.46px tracking, 22px icons, 4px corners
const PLAY_BUTTON_TEXT = {
  fontSize: '15px',
  fontWeight: 500,
  lineHeight: '26px',
  letterSpacing: '0.46px',
  borderRadius: '4px',
  gap: '8px',
};
const PLAY_ICON_PROPS = { sx: { fontSize: 22 } };
const LINK_BUTTON_SX = { ...PLAY_BUTTON_TEXT, padding: '8px 11px' };
const RESPONSE_OPTIONS = [PlaybookResponse.Yes, PlaybookResponse.ToDo, PlaybookResponse.No];

// Router state passed between plays
export interface PlaybookNavState {
  fromPrevPlay?: boolean;
  // Set on every Prev/Next navigation so the destination play tracks its Open event
  fromPlaybookNav?: boolean;
}

interface PlaybookActionsProps {
  contentId: number;
  // Preview content isn't live, so responses can't be saved
  disabled?: boolean;
}

/**
 * Prev Play / "Running this play?" / Next Play, shown in the back-button row on playbooks.
 */
export default function PlaybookActions({ contentId, disabled = false }: PlaybookActionsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = !useIsDesktop();
  // Set by Prev Play's navigation, so history only steps back on a real Prev click
  const cameFromPrev = !!(location.state as PlaybookNavState | null)?.fromPrevPlay;
  const prevId = usePlaybookHistory(contentId, cameFromPrev);
  // Next is greyed out only while it's looking up where to go
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [response, setResponse] = useState<PlaybookResponse>();
  // The status button shows a skeleton until this play's saved answer has loaded
  const [isResponseLoading, setIsResponseLoading] = useState(!disabled);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Cleared when the play changes or the component unmounts, so late results are ignored
  const activeRef = useRef<{ active: boolean }>({ active: true });

  useEffect(() => {
    const token = { active: true };
    activeRef.current = token;
    return () => { token.active = false; };
  }, [contentId]);

  useEffect(() => {
    let active = true;
    setResponse(undefined);
    setIsNextLoading(false);
    setIsResponseLoading(!disabled);
    if (disabled) return undefined;

    contentAPI.getPlaybookResponse(contentId)
      .then((record) => { if (active) setResponse(record?.response); })
      // On failure the button still appears, just with nothing selected
      .catch((err) => logger.error(Logger.format(err)))
      .finally(() => { if (active) setIsResponseLoading(false); });
    return () => { active = false; };
  }, [contentId, disabled]);

  // Replace history so "Back to All Plays" leaves the playbooks instead of stepping through them
  const goTo = (id: number, state: PlaybookNavState = {}) => navigate(
    `/content/${id}`,
    { replace: true, state: { ...state, fromPlaybookNav: true } },
  );

  // The next play is only looked up when Next is clicked, so page views don't query Strapi
  const onNextClick = async () => {
    const token = activeRef.current;
    setIsNextLoading(true);
    try {
      const id = await contentAPI.getNextPlaybookId(contentId);
      // The member may have left this play (e.g. Back to All Plays) while it was loading
      if (token.active && id !== null) goTo(id);
    } catch (err) {
      logger.error(Logger.format(err));
    } finally {
      if (token.active) setIsNextLoading(false);
    }
  };

  const onSelect = (value: PlaybookResponse) => {
    setMenuAnchor(null);
    // Keep the choice on screen even if saving fails; it resets on reload or leaving this play
    setResponse(value);
    contentAPI.savePlaybookResponse(contentId, value)
      .catch((err) => logger.error(Logger.format(err)));
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      // Below desktop Prev/Next sit at either edge and the status button spans the row below
      [theme.breakpoints.down('md')]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateAreas: '"prev next" "status status"',
        rowGap: theme.spacing(1.25),
        width: '100%',
      },
    }}>
      <Box sx={{ gridArea: 'prev', justifySelf: 'start' }}>
        <OptimusButton
          size='m'
          fullWidth={false}
          minWidth='auto'
          customVariant='link'
          data-testid={testIds.prevBtn}
          sx={LINK_BUTTON_SX}
          iconProps={PLAY_ICON_PROPS}
          leadingIcon={ChevronLeftIcon}
          buttonText={`${t('content.playbook.prevPlay')}`}
          disabled={prevId === undefined}
          onClick={() => prevId !== undefined && goTo(prevId, { fromPrevPlay: true })}
        />
      </Box>
      <Box sx={{ gridArea: 'status', display: 'flex' }}>
        {isResponseLoading ?
          <Skeleton
            data-testid={testIds.responseSkeleton}
            animation='wave'
            variant='rounded'
            width={isMobile ? '100%' : theme.spacing(21)}
            height={theme.spacing(5)}
            sx={{ borderRadius: theme.spacing(0.5) }}
          /> :
          <OptimusButton
            size='m'
            fullWidth={isMobile}
            minWidth='auto'
            // Once answered the button is outlined in the chosen option's color
            customVariant={response ? 'secondaryGray' : 'primary'}
            sx={response ? {
              color: RESPONSE_COLORS[response],
              borderColor: RESPONSE_COLORS[response],
              '&:hover': { backgroundColor: alpha(RESPONSE_COLORS[response], 0.04) },
            } : undefined}
            data-testid={testIds.responseBtn}
            trailingIcon={ExpandMoreIcon}
            buttonText={response ?
              `${t('content.playbook.runningThisPlayWithResponse', {
                response: t(`content.playbook.response.${response}`),
              })}` :
              `${t('content.playbook.runningThisPlay')}`}
            disabled={disabled}
            onClick={(e: MouseEvent<HTMLButtonElement>) => setMenuAnchor(e.currentTarget)}
          />
        }
      </Box>
      <Menu
        data-testid={testIds.responseMenu}
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // The theme strips every Paper's shadow; a floating menu needs one (as in ChatHistoryList)
        PaperProps={{
          sx: { width: 205, borderRadius: theme.spacing(0.5), boxShadow: theme.shadows[8] },
        }}
      >
        {RESPONSE_OPTIONS.map((value) => (
          <MenuItem
            key={value}
            data-testid={`${testIds.responseOption}-${value}`}
            selected={response === value}
            onClick={() => onSelect(value)}
            sx={{
              py: 0.75,
              px: 2,
              fontSize: 16,
              lineHeight: 1.5,
              letterSpacing: '0.15px',
              color: RESPONSE_COLORS[value],
            }}
          >
            {t(`content.playbook.response.${value}`)}
          </MenuItem>
        ))}
      </Menu>
      <Box sx={{ gridArea: 'next', justifySelf: 'end' }}>
        <OptimusButton
          size='m'
          fullWidth={false}
          minWidth='auto'
          customVariant='link'
          data-testid={testIds.nextBtn}
          sx={LINK_BUTTON_SX}
          iconProps={PLAY_ICON_PROPS}
          trailingIcon={ChevronRightIcon}
          buttonText={`${t('content.playbook.nextPlay')}`}
          disabled={disabled || isNextLoading}
          onClick={onNextClick}
        />
      </Box>
    </Box>
  );
}
